import { GuideTopic, UserGuideConfig } from '../types';

const VOID_HTML_TAGS = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
]);

export const formatDateTimeDisplayValue = (value: string) => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleString();
};

export const duplicateGuideTopic = (topic: GuideTopic): GuideTopic => ({
  ...topic,
  id: `topic-${Math.random().toString(36).slice(2, 10)}`,
  title: `${topic.title} Copy`,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

const getTagName = (rawTag: string) =>
  rawTag
    .replace(/^<\/?/, '')
    .replace(/\/?>$/, '')
    .trim()
    .split(/\s+/)[0]
    .toLowerCase();

const getHtmlTagValidationError = (html: string) => {
  const openTags: string[] = [];
  const tagPattern = /<!--[\s\S]*?-->|<![^>]*>|<[^>]*>/g;
  let match: RegExpExecArray | null;

  while ((match = tagPattern.exec(html)) !== null) {
    const rawTag = match[0];

    if (
      rawTag.startsWith('<!--') ||
      rawTag.startsWith('<!') ||
      rawTag.startsWith('<?')
    ) {
      continue;
    }

    const tagName = getTagName(rawTag);
    if (!tagName || VOID_HTML_TAGS.has(tagName)) continue;

    const isClosingTag = rawTag.startsWith('</');
    const isSelfClosingTag = rawTag.endsWith('/>');

    if (isSelfClosingTag) continue;

    if (!isClosingTag) {
      openTags.push(tagName);
      continue;
    }

    const expectedTag = openTags.pop();
    if (!expectedTag) {
      return `Unexpected closing tag </${tagName}>.`;
    }

    if (expectedTag !== tagName) {
      return `HTML tag mismatch: expected </${expectedTag}> before </${tagName}>.`;
    }
  }

  if (openTags.length) {
    return `Unclosed HTML tag <${openTags[openTags.length - 1]}>.`;
  }

  return '';
};

export const getGuideValidationErrors = (config: UserGuideConfig) => {
  const errors: string[] = [];

  if (
    !Number.isFinite(config.announcementHighlightDays) ||
    config.announcementHighlightDays < 1 ||
    config.announcementHighlightDays > 90
  ) {
    errors.push('Announcement highlight days must be between 1 and 90.');
  }

  config.tabs.forEach(tab => {
    if (!tab.label.trim()) {
      errors.push('Every tab needs a label.');
    }

    tab.topics.forEach((topic, topicIndex) => {
      const topicName = topic.title.trim() || `Topic ${topicIndex + 1}`;

      if (!topic.title.trim()) {
        errors.push(`${tab.label}: every topic needs a title.`);
      }

      if (!topic.contentHtml.trim()) {
        errors.push(`${tab.label} / ${topicName}: HTML content is required.`);
      }

      const htmlTagError = getHtmlTagValidationError(topic.contentHtml);
      if (htmlTagError) {
        errors.push(`${tab.label} / ${topicName}: ${htmlTagError}`);
      }
    });
  });

  config.mediaLibrary.forEach((asset, index) => {
    if (!asset.name.trim()) {
      errors.push(`Media ${index + 1}: name is required.`);
    }

    if (!asset.src?.trim()) {
      errors.push(`${asset.name || `Media ${index + 1}`}: source is missing.`);
    }
  });

  return errors;
};
